import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon, ClockIcon, SendIcon, CheckCircleIcon } from 'lucide-react';
export function ContactPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div data-id="element-585">
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden bg-slate-900" data-id="element-586">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-587"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-588"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-589"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-590">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-591">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-592">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-593">
              For additional information on our initiatives or products, please
              fill out the requested information below and reference the product
              you would like to learn more about.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-slate-100" data-id="element-594">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-595">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl" data-id="element-596">
            {/* Left - Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-slate-900 p-10 md:p-14 flex flex-col justify-center" data-id="element-597">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10" data-id="element-598">
                Contact Us
              </h2>

              <div className="space-y-8" data-id="element-599">
                <div className="flex items-start gap-4" data-id="element-600">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-601">
                    <MapPinIcon className="w-5 h-5 text-primary" data-id="element-602" />
                  </div>
                  <div data-id="element-603">
                    <p className="text-white leading-relaxed" data-id="element-604">
                      Century Plaza Office Center, 10632 Little Patuxent Pkwy.
                      Suite 302
                      <br data-id="element-605" />
                      Columbia, MD 21044
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-id="element-606">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-607">
                    <PhoneIcon className="w-5 h-5 text-primary" data-id="element-608" />
                  </div>
                  <a href="tel:+4105255284" className="text-white hover:text-primary transition-colors font-medium" data-id="element-609">
                    410-525-5284
                  </a>
                </div>

                <div className="flex items-center gap-4" data-id="element-610">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-611">
                    <ClockIcon className="w-5 h-5 text-primary" data-id="element-612" />
                  </div>
                  <p className="text-white" data-id="element-613">
                    Mon – Fri – 9am – 5pm | Sat 9am – 5pm
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white p-10 md:p-14" data-id="element-614">
              {submitted ? <div className="flex flex-col items-center justify-center h-full text-center py-10" data-id="element-615">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-616">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-617" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-618">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600 mb-6 max-w-sm" data-id="element-619">
                    Thank you for reaching out. Our team will get back to you
                    within 24 hours.
                  </p>
                  <button onClick={() => {
                setSubmitted(false);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  message: ''
                });
              }} className="text-primary font-semibold hover:underline" data-id="element-620">
                    Send another message
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-621">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-622">
                    <div data-id="element-623">
                      <label htmlFor="firstName" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-624">
                        First Name
                      </label>
                      <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter your first name" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-625" />
                    </div>
                    <div data-id="element-626">
                      <label htmlFor="lastName" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-627">
                        Last Name
                      </label>
                      <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter your last name" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-628" />
                    </div>
                  </div>

                  <div data-id="element-629">
                    <label htmlFor="email" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-630">
                      Email
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email address" required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-631" />
                  </div>

                  <div data-id="element-632">
                    <label htmlFor="phone" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-633">
                      Phone
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" data-id="element-634" />
                  </div>

                  <div data-id="element-635">
                    <label htmlFor="message" className="block text-sm font-bold text-slate-800 mb-2" data-id="element-636">
                      Message
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="How can we help you?" rows={5} required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-vertical" data-id="element-637" />
                  </div>

                  <button type="submit" className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 px-10 rounded-lg transition-colors inline-flex items-center justify-center" data-id="element-638">
                    Submit
                    <SendIcon className="w-4 h-4 ml-2" data-id="element-639" />
                  </button>
                </form>}
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}